<template>
  <div
    :class="[
      'product-identifier-fi',
      `product-identifier-fi--${shopType}`,
      showVariantImage ? `product-identifier-fi--with-image` : '',
    ]"
    @click.stop
  >
    <div
      v-if="showVariantImage"
      class="product-identifier-fi__product-image"
    >
      <a
        class="product-identifier-fi__product-image__link"
        :href="getSlugLink(item.catalogProductData) || undefined"
        :title="item.catalogProductData.ProductName"
        @link-click="onClick"
      >
        <cloud-image-fi
          v-if="previewImage"
          :src="previewImage"
          :alt="item.name"
          :width="isLG ? '90' : '67'"
          :height="isLG ? '90' : '67'"
          trim
          params="func=fit&bg_colour=FFF"
          class="product-identifier-fi__product-image__image"
        />
      </a>
    </div>

    <div class="product-identifier-fi__data">
      <price-savings-in-percent-fi
        class="product-identifier-fi__price-savings"
        :best-price="bestPrice"
        theme="product-list-view"
      />

      <link-fi
        ref="productName"
        class="product-identifier-fi__title"
        primary
        :theme="hasLongTitle ? 'product-list-view' : ''"
        :href="getSlugLink(item.catalogProductData)"
        :title="item.catalogProductData.ProductName"
        :size="16"
        @link-click="onClick"
      >
        <span ref="productNameFull">
          {{ item.catalogProductData.ProductName }}
        </span>
      </link-fi>

      <div
        v-if="item.catalogProductData.ProductId"
        class="product-identifier-fi__article-number"
        data-test="article-number"
      >
        {{ $dict.get('product-List.Table.Article-Number') }} {{ item.catalogProductData.ProductId }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { CloudImageFi, LinkFi, PriceSavingsInPercentFi } from 'atoms';
import { throttle } from 'throttle-debounce';
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue';

import type { BestPrice } from '@/composables/bestPrice';
import { useBreakpoints } from '@/composables/breakpoint';
import { useEventHub } from '@/composables/eventHub';
import { useSlugLink } from '@/composables/slugLink';
import { useNavigationStore } from '@/store/pinia/navigation';
import { useProductListStore } from '@/store/pinia/product-list';

defineOptions({ name: 'ProductIdentifierFi' });

const props = withDefaults(
  defineProps<{
    item?: any | null;
    bestPrice?: BestPrice | null;
    clickSource?: string | null;
    showVariantImage?: boolean;
  }>(),
  {
    item: null,
    bestPrice: null,
    clickSource: null,
    showVariantImage: false,
  },
);

const productListStore = useProductListStore();
const navigationStore = useNavigationStore();
const { isLG, windowDimensions } = useBreakpoints();
const { sendClickToEventHub } = useEventHub();
const { getSlugLink } = useSlugLink();

const productName = useTemplateRef('productName');
const productNameFull = useTemplateRef('productNameFull');

const hasLongTitle = ref(false);

const shopType = computed(() => productListStore.shopType);

const previewImage = computed(
  () => (props.item as any)?.catalogProductData?.packagingImage || (props.item as any)?.ImageURL,
);

const updateProductLink = () => {
  if (IS_SSR || !productName.value || !productName.value.$el || !productNameFull.value) {
    return;
  }

  hasLongTitle.value =
    productNameFull.value.offsetHeight > productName.value.$el.offsetHeight || false;
};

let throttledUpdateProductLink: ReturnType<typeof throttle> | null = null;

watch(
  windowDimensions,
  () => {
    throttledUpdateProductLink?.();
  },
  { deep: true },
);

onMounted(() => {
  if (IS_SSR) {
    return;
  }
  updateProductLink();
  throttledUpdateProductLink = throttle(200, updateProductLink);
});

const onClick = (event: MouseEvent, href: string | null) => {
  if (!props.clickSource || !href) {
    return;
  }
  const productBreadcrumb = navigationStore.productBreadcrumb;
  const productString = productBreadcrumb.map((item: any) => item.title).join('/');
  const item = props.item as any;
  sendClickToEventHub(
    {
      url: href,
      clickSource: props.clickSource,
      pageTitle: item.catalogProductData.ProductName,
      productId: item.catalogProductData.ProductId,
      productTitle: item.catalogProductData.ProductName,
      query: `[${props.clickSource}]:/${productString}/${item.catalogProductData.ProductName}`,
    },
    event,
    href,
  );
};
</script>

<style scoped src="./product-identifier-fi" />
